import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_OPTIONS } from '../constants';
import { apiClient } from '../../../config/axios.config';
import { BASE_EMPLOYEE_URL } from '../../../store/apiConfig';

const currentEntityCode = localStorage.getItem('entityCode') || '';
/**
 * Custom hook to load dropdown options for the Employee Master.
 * Uses the EMPMAST_LOAD code list for efficient batch data retrieval.
 */
export function useDropdowns() {
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const ignoreDropdownLoadError = () => {};

  useEffect(() => {
    // Using apiClient automatically handles BASE_URL and Authorization headers
    const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
    const currentEmpId = authUser.userName || '';

    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'EMPMAST_LOAD',
        param1: '',
        param2: '',
        param3: '',
        param4: currentEntityCode,
        employeeId: currentEmpId,
      })
      .then(data => {
        // apiClient interceptor returns response.data directly
        const resultList = data.result || [];

        const pickFirstNonEmpty = (...values) =>
          values
            .map(value => {
              if (value === undefined || value === null) return '';
              return String(value).trim();
            })
            .find(Boolean) || '';

        // Helper to extract list from categories (handles various API key casings)
        const filterCategory = cat =>
          resultList
            .filter(item => {
              const itemCat = item.Category || item.CATEGORY;
              return (
                itemCat && itemCat.trim().toUpperCase() === cat.toUpperCase()
              );
            })
            .map(item => {
              const val = pickFirstNonEmpty(
                item.RowValue,
                item.NAME,
                item.VAL,
                item.RowKey,
                item.CODE,
              );
              const key = pickFirstNonEmpty(
                item.RowKey,
                item.CODE,
                item.VAL,
                item.RowValue,
                item.NAME,
              );
              return {
                value: key,
                label: val,
              };
            });

        // Organize the data as requested by the user
        const weeklyHolidayDefaultRaw = pickFirstNonEmpty(
          ...resultList
            .filter(item => {
              const itemCat = item.Category || item.CATEGORY;
              return (
                itemCat &&
                itemCat.trim().toUpperCase() === 'WK_HOLIDAY'.toUpperCase()
              );
            })
            .map(item => item.RowValue || item.RowKey || item.VAL || item.CODE),
        );

        const weeklyHolidayDefault = weeklyHolidayDefaultRaw
          .split(',')
          .map(item => Number(String(item || '').trim()))
          .filter(num => Number.isInteger(num) && num >= 0);

        const dropdownData = {
          // Bank & Payments
          bankCode: filterCategory('Bank'),
          banks: filterCategory('Bank'),
          bankBranch: filterCategory('BankBranch'),
          payMode: filterCategory('PayMode'),
          payCurrency: filterCategory('Currency'),
          payGroup: filterCategory('BankGrp'),

          // Identity & Personal
          nationality: filterCategory('Country'),
          bloodGroup: filterCategory('BloodGroup'),
          religion: filterCategory('Religion'),
          maritalStatus: filterCategory('Marital'),
          status: filterCategory('Status'),

          // Organization
          department: filterCategory('Dept'),
          costCentre: filterCategory('CC'),
          desgInternal: filterCategory('Designation'),
          apprGroup: filterCategory('ApprovalGrp'),
          classification: filterCategory('Classification1'),
          classification2: filterCategory('Classification2'),
          entity: filterCategory('Entity'),
          section: filterCategory('Section'),

          // Schemes
          bonusScheme: filterCategory('Bonus'),
          pcmScheme: filterCategory('PCM'),

          // Others
          location: filterCategory('Location'),
          holidayGrp: filterCategory('HolidayGrp'),
          ticketsContract: filterCategory('TICKET'),
          ticketsActual: filterCategory('TICKET'),
          travelSector: filterCategory('TravSect'),
          travelClass: filterCategory('TravelClass'),
          accrualType: filterCategory('AccrualType'),
          annualLeaveDays: filterCategory('LVDAYS'),
          issuePlace: filterCategory('Country'),
          sponsor: filterCategory('Sponsor'),
          country: filterCategory('Country'),
          weeklyHolidayDefault,
        };

        setOptions(prev => ({
          ...prev,
          ...dropdownData,
        }));
      })
      .catch(ignoreDropdownLoadError);
  }, []);

  /** Fetches bank branches for a specific bank code */
  const fetchBankBranches = (bankCode, empCode) => {
    if (!bankCode) return;
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'BANK_BRANCH',
        param1: bankCode,
        param2: '',
        param3: '',
        param4: currentEntityCode,
        employeeId: empCode || '',
      })
      .then(data => {
        const resultList = data.result || [];
        const branches = resultList.map(item => ({
          value: String(item.Branch || '').trim(),
          label: String(item.BranchName || '').trim(),
        }));
        setOptions(prev => ({ ...prev, bankBranch: branches }));
      })
      .catch(ignoreDropdownLoadError);
  };

  /** Fetches Relation list */
  const fetchRelation = empCode => {
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'CODES_GEN',
        param1: 'HRELN',
        param2: '',
        param3: '',
        param4: '',
        employeeId: empCode || '',
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, relation: list }));
      })
      .catch(ignoreDropdownLoadError);
  };

  /** Fetches Facility types */
  const fetchFacilityTypes = useCallback(empCode => {
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'CODES_GEN',
        param1: 'FCTYP',
        param2: '',
        param3: '',
        param4: '',
        employeeId: empCode || '',
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, facilityTypes: list }));
      })
      .catch(ignoreDropdownLoadError);
  }, []);

  /** Fetches Facility categories based on chosen facility type */
  const fetchFacilityCategories = useCallback((facilityType, empCode) => {
    if (!facilityType) return;
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'CODES_GEN',
        param1: 'FCCAT',
        param2: facilityType,
        param3: '',
        param4: '',
        employeeId: empCode || '',
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, facilityCategories: list }));
      })
      .catch(ignoreDropdownLoadError);
  }, []);

  /** Fetches Attachment types */
  const fetchAttachmentTypes = empCode => {
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'CODES_GEN',
        param1: 'JNDOC',
        param2: '',
        param3: '',
        param4: '',
        employeeId: empCode || '',
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, attachmentTypes: list }));
      })
      .catch(ignoreDropdownLoadError);
  };

  /** Fetches Documents types */
  const fetchDocumentTypes = empCode => {
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'CODES_GEN',
        param1: 'HDTYP',
        param2: '',
        param3: '',
        param4: '',
        employeeId: empCode || '',
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, attachmentTypes: list }));
      })
      .catch(ignoreDropdownLoadError);
  };

  /** Fetches Dependents for an employee */
  const fetchDependents = useCallback(empCode => {
    if (!empCode) return;
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/get-hrcodelist`, {
        codeType: 'DEPENDENT',
        param1: '',
        param2: '',
        param3: '',
        param4: '',
        employeeId: empCode,
      })
      .then(data => {
        const list = (data.result || []).map(item => ({
          value: String(
            item.Code || item.CODE || item.VAL || item.RowKey || '',
          ).trim(),
          label: String(
            item.Description || item.Name || item.NAME || item.RowValue || '',
          ).trim(),
        }));
        setOptions(prev => ({ ...prev, dependents: list }));
      })
      .catch(ignoreDropdownLoadError);
  }, []);

  return {
    options,
    fetchBankBranches,
    fetchRelation,
    fetchFacilityTypes,
    fetchFacilityCategories,
    fetchAttachmentTypes,
    fetchDocumentTypes,
    fetchDependents,
  };
}
